import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import ReactQuill, { Quill } from "react-quill-new";
import QuillTableBetter from "quill-table-better";
import {
	Button,
	Paper,
	Popper,
	Stack,
	TextField,
} from "@mui/material";
import "react-quill-new/dist/quill.snow.css";
import "quill-table-better/dist/quill-table-better.css";
import { setMonthlyCostStatusEmailFormField } from "../../../../store/slices/projectSlice";

Quill.register({ "modules/table-better": QuillTableBetter }, true);

const EMAIL_RE = /^\S+@\S+\.\S+$/;
const CLOSED_LINK_UI = { open: false, mode: null, range: null, value: "" };

const useReduxSelectors = () => {
	const { monthlyCostStatusEmailFormData } = useSelector(
		(state) => state.project
	);
	return { monthlyCostStatusEmailFormData };
};

// Normalize raw input into a hyperlink-ready URL.
const normalizeUrl = (raw) => {
	const value = (raw || "").trim();
	if (!value) return "";
	if (EMAIL_RE.test(value) && !value.startsWith("mailto:"))
		return `mailto:${value}`;
	if (/^[a-z][a-z0-9+.-]*:/i.test(value)) return value;
	return `https://${value}`;
};

// If `range` lands inside a link blot, return the full extent of that link
// plus its href value. Otherwise null.
const getLinkAtRange = (quill, range) => {
	if (!quill || !range) return null;
	try {
		const LinkBlot = Quill.import("formats/link");
		const [link, offset] = quill.scroll.descendant(LinkBlot, range.index);
		if (!link) return null;
		return {
			range: { index: range.index - offset, length: link.length() },
			value: LinkBlot.formats(link.domNode),
		};
	} catch {
		return null;
	}
};

// Build a Popper virtual anchor from a Quill range using getBounds (container-
// relative) plus the container's viewport rect.
const getVirtualAnchor = (quill, range) => ({
	contextElement: quill.root,
	getBoundingClientRect: () => {
		const bounds = quill.getBounds(range);
		const container = quill.container.getBoundingClientRect();
		const width = Math.max(bounds?.width ?? 1, 1);
		const height = Math.max(bounds?.height ?? 16, 16);
		const left = container.left + (bounds?.left ?? 0);
		const top = container.top + (bounds?.top ?? 0);
		return new DOMRect(left, top, width, height);
	},
});

export default function EditorComponent({ fieldId, value }) {
	const dispatch = useDispatch();
	const { monthlyCostStatusEmailFormData } = useReduxSelectors();
	const quillRef = useRef(null);
	const lastAppliedHtmlRef = useRef("");
	const isHydratingRef = useRef(false);

	// Custom link UI. One Popper, two modes:
	//   preview: cursor sits inside an existing link -> Visit / Edit / Remove
	//   edit:    create new or edit existing -> TextField / Save / Cancel
	// We render everything inside this component's tree, so the popper lives
	// inside whichever Modal/Dialog hosts the editor (no FocusTrap fight, no
	// portal reparenting). We also CSS-hide Quill's stock .ql-tooltip so the
	// user never sees two different link UIs at once.
	const [linkUi, setLinkUi] = useState(CLOSED_LINK_UI);
	const linkUiRef = useRef(linkUi);
	useEffect(() => {
		linkUiRef.current = linkUi;
	}, [linkUi]);
	const linkInputRef = useRef(null);

	const modules = useMemo(
		() => ({
			toolbar: {
				container: [
					["bold", "italic", "underline", "strike"],
					[{ color: [] }, { background: [] }],
					["link"],
					[{ list: "ordered" }, { list: "bullet" }],
					["clean"],
					["table-better"],
				],
				handlers: {
					// Open our edit popper regardless of whether there's an
					// existing link at the cursor. (Removal is an explicit
					// action in preview mode, not a side effect of clicking
					// the toolbar button on an active link.)
					link: function () {
						const quill = this.quill;
						const range = quill.getSelection(true) || {
							index: quill.getLength(),
							length: 0,
						};
						const existing = getLinkAtRange(quill, range);
						if (existing) {
							setLinkUi({ open: true, mode: "edit", ...existing });
							return;
						}
						const selectedText =
							range.length > 0 ? quill.getText(range).trim() : "";
						const initialValue = EMAIL_RE.test(selectedText)
							? `mailto:${selectedText}`
							: "";
						setLinkUi({
							open: true,
							mode: "edit",
							range,
							value: initialValue,
						});
					},
				},
			},
			table: false,
			"table-better": {
				language: "en_US",
				menus: [
					"column",
					"row",
					"merge",
					"table",
					"cell",
					"wrap",
					"delete",
				],
				toolbarTable: true,
				copyWithHeaders: true,
			},
			keyboard: {
				bindings: QuillTableBetter.keyboardBindings,
			},
		}),
		[]
	);

	const hydrateFromHtml = useCallback((html) => {
		const quill = quillRef.current?.getEditor?.();
		if (!quill || typeof html !== "string") return;
		if (html === lastAppliedHtmlRef.current) return;

		isHydratingRef.current = true;
		try {
			quill.setText("", Quill.sources.SILENT);
			const delta = quill.clipboard.convert({ html });
			quill.updateContents(delta, Quill.sources.SILENT);
			const len = Math.max(0, (quill.getLength?.() ?? 1) - 1);
			quill.setSelection(len, 0, Quill.sources.SILENT);
			quill.scrollSelectionIntoView?.();
			lastAppliedHtmlRef.current = html;
		} catch (error) {
			console.error("Error during HTML hydration:", error);
			try {
				quill.setText(html.replace(/<[^>]*>/g, ""), Quill.sources.SILENT);
			} catch (fallbackError) {
				console.error("Fallback hydration also failed:", fallbackError);
			}
		} finally {
			isHydratingRef.current = false;
		}
	}, []);

	const onChange = (content, delta, source, editor) => {
		if (isHydratingRef.current) return;
		if (source !== "user") return;

		const html = editor?.getHTML?.() ?? "";
		lastAppliedHtmlRef.current = html;
		const key = fieldId;

		if (monthlyCostStatusEmailFormData && key) {
			const prev = monthlyCostStatusEmailFormData[key] ?? "";
			if (html !== prev) {
				dispatch(setMonthlyCostStatusEmailFormField({ key, value: html }));
			}
		}
	};

	useEffect(() => {
		if (value !== lastAppliedHtmlRef.current) {
			setTimeout(() => hydrateFromHtml(value), 0);
		}
	}, [value, hydrateFromHtml]);

	// Auto-focus the link input when entering edit mode.
	useEffect(() => {
		if (linkUi.open && linkUi.mode === "edit" && linkInputRef.current) {
			linkInputRef.current.focus();
			linkInputRef.current.select();
		}
	}, [linkUi.open, linkUi.mode]);

	// Existing-link preview: open the preview popper when the cursor lands
	// inside a link, close it otherwise. Stay out of edit mode's way.
	useEffect(() => {
		const quill = quillRef.current?.getEditor?.();
		if (!quill) return;
		const onSelectionChange = (range, _old, source) => {
			if (linkUiRef.current.mode === "edit") return;
			if (!range || range.length !== 0 || source !== "user") {
				if (linkUiRef.current.mode === "preview") setLinkUi(CLOSED_LINK_UI);
				return;
			}
			const existing = getLinkAtRange(quill, range);
			if (existing) {
				setLinkUi({ open: true, mode: "preview", ...existing });
			} else if (linkUiRef.current.mode === "preview") {
				setLinkUi(CLOSED_LINK_UI);
			}
		};
		quill.on("selection-change", onSelectionChange);
		return () => quill.off("selection-change", onSelectionChange);
	}, []);

	// Table-cell input fix: QuillTableBetter's beforeinput handler ejects the
	// cursor on certain inputs. For non-whitespace input inside a cell, stop
	// propagation and insert via the Quill API. For whitespace, just stop
	// propagation so the native input handler renders spaces correctly.
	useEffect(() => {
		const quill = quillRef.current?.getEditor?.();
		if (!quill) return;

		const handleBeforeInput = (e) => {
			const selection = quill.getSelection();
			if (!selection) return;
			const [leaf] = quill.getLeaf(selection.index);
			const tableCell = leaf?.parent?.domNode?.closest("td, th");
			if (tableCell) {
				e.stopPropagation();
				if (e.data && e.data.trim().length > 0) {
					e.preventDefault();
					quill.insertText(selection.index, e.data, "user");
					quill.setSelection(selection.index + e.data.length, 0, "user");
				}
			}
		};

		const editorElement = quill.root;
		editorElement.addEventListener("beforeinput", handleBeforeInput, true);
		return () =>
			editorElement.removeEventListener(
				"beforeinput",
				handleBeforeInput,
				true
			);
	}, []);

	const handleLinkSave = () => {
		const quill = quillRef.current?.getEditor?.();
		const { range, value: raw } = linkUi;
		const url = normalizeUrl(raw);
		if (!quill || !range || !url) {
			setLinkUi(CLOSED_LINK_UI);
			return;
		}
		if (range.length > 0) {
			quill.formatText(range.index, range.length, "link", url, "user");
			quill.setSelection(range.index + range.length, 0, "user");
		} else {
			quill.insertText(range.index, url, { link: url }, "user");
			quill.setSelection(range.index + url.length, 0, "user");
		}
		setLinkUi(CLOSED_LINK_UI);
	};

	const handleLinkRemove = () => {
		const quill = quillRef.current?.getEditor?.();
		const { range } = linkUi;
		if (!quill || !range) {
			setLinkUi(CLOSED_LINK_UI);
			return;
		}
		quill.formatText(range.index, range.length, "link", false, "user");
		setLinkUi(CLOSED_LINK_UI);
	};

	const handleLinkCancel = () => {
		setLinkUi(CLOSED_LINK_UI);
	};

	const quill = quillRef.current?.getEditor?.();
	const anchorEl =
		quill && linkUi.open && linkUi.range
			? getVirtualAnchor(quill, linkUi.range)
			: null;

	return (
		<div className="phd-editor" style={{ position: "relative" }}>
			<style>
				{`
					/* Suppress Quill's stock Snow tooltip so we never show two
					   link UIs at once. Our React popper handles all flows. */
					.phd-editor .ql-tooltip {
						display: none !important;
						pointer-events: none !important;
					}

					.ql-editor table td,
					.ql-editor table th {
						word-wrap: break-word !important;
						word-break: break-word !important;
						white-space: pre-wrap !important;
						overflow-wrap: break-word !important;
						padding: 4px !important;
						margin: 0 !important;
						border: 1px solid #ccc !important;
						vertical-align: top !important;
						min-width: 80px !important;
						width: auto !important;
						max-width: none !important;
						height: auto !important;
						min-height: 20px !important;
						box-sizing: border-box !important;
					}
					.ql-editor table {
						table-layout: auto !important;
						width: 100% !important;
						border-collapse: collapse !important;
						margin: 10px 0 !important;
					}
					.ql-editor table td > *,
					.ql-editor table th > * {
						margin: 0 !important;
						padding: 0 !important;
						line-height: 1.4 !important;
					}
					.ql-editor table td p,
					.ql-editor table th p {
						margin: 0 !important;
						padding: 0 !important;
					}
					.ql-editor table td:focus,
					.ql-editor table th:focus {
						outline: 2px solid #0066cc !important;
						outline-offset: -1px !important;
					}
				`}
			</style>
			<ReactQuill
				ref={quillRef}
				theme="snow"
				onChange={onChange}
				modules={modules}
			/>
			<Popper
				open={linkUi.open}
				anchorEl={anchorEl}
				placement="bottom-start"
				disablePortal
				modifiers={[
					{ name: "offset", options: { offset: [0, 6] } },
					{ name: "flip", options: { padding: 8 } },
					{
						name: "preventOverflow",
						options: { padding: 8, altAxis: true },
					},
				]}
				sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}
			>
				<Paper elevation={4} sx={{ p: 1, maxWidth: 480 }}>
					{linkUi.mode === "preview" ? (
						<Stack
							direction="row"
							spacing={1}
							alignItems="center"
							sx={{ fontSize: 13 }}
						>
							<Button
								size="small"
								onClick={() =>
									window.open(
										linkUi.value,
										"_blank",
										"noopener,noreferrer"
									)
								}
							>
								Visit
							</Button>
							<Button
								size="small"
								onClick={() =>
									setLinkUi((p) => ({ ...p, mode: "edit" }))
								}
							>
								Edit
							</Button>
							<Button
								size="small"
								color="error"
								onClick={handleLinkRemove}
							>
								Remove
							</Button>
						</Stack>
					) : (
						<Stack
							direction="row"
							spacing={1}
							alignItems="center"
						>
							<TextField
								inputRef={linkInputRef}
								size="small"
								placeholder="https://example.com"
								value={linkUi.value}
								onChange={(e) =>
									setLinkUi((p) => ({
										...p,
										value: e.target.value,
									}))
								}
								onKeyDown={(e) => {
									if (e.key === "Enter") {
										e.preventDefault();
										handleLinkSave();
									} else if (e.key === "Escape") {
										e.preventDefault();
										handleLinkCancel();
									}
								}}
								sx={{ minWidth: 280 }}
							/>
							<Button
								size="small"
								variant="contained"
								onClick={handleLinkSave}
							>
								Save
							</Button>
							<Button size="small" onClick={handleLinkCancel}>
								Cancel
							</Button>
						</Stack>
					)}
				</Paper>
			</Popper>
		</div>
	);
}
